import React from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import Modal from 'modules/views/shared/Modal';
import { ReactComponent as ExclamationRound } from 'assets/images/ExclamationRound.svg';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { deleteCertification, setOnboarding } from 'modules/state-mgmt/onboarding';

import { actionMapForOtherLoaders } from 'constants/actions';
import { useStylesDeleteCertificationModal } from '../styles';
import { Certificate } from './CertificateCard';

interface IDeleteCertificationModalProps {
  certificate: Certificate;
  onClose: () => void;
  onDeleteSuccess: () => void;
  isVisible: boolean;
}

const DeleteCertificationModal = ({
  certificate,
  onClose,
  onDeleteSuccess,
  isVisible,
}: IDeleteCertificationModalProps) => {
  const { t } = useTranslation();
  const classes = useStylesDeleteCertificationModal();
  const dispatch = useAppDispatch();
  const { workerId, isDeleteCertificationLoading, languageCode, isOffline, isWorker, onboarding } = useAppSelector(
    state => ({
      workerId: state.onboarding.id,
      isDeleteCertificationLoading: state.loader.otherLoaders[actionMapForOtherLoaders.deleteCertification]?.isLoading,
      languageCode: state.app?.languageCode,
      isOffline: !state.app.isOnline || state.onboarding?.uploadingDetails?.isOffline,
      isWorker: !state.user?.userId || state.user?.isWorker,
      onboarding: state.onboarding,
    }),
  );

  const onDeleteCertification = () => {
    if (isOffline && !isWorker) {
      const currentItems = (onboarding.allCertifications as any)?.items || [];
      const updatedItems = certificate.tempId
        ? currentItems.filter((c: any) => c.id !== certificate.id)
        : currentItems.map((c: any) => (c.id === certificate.id ? { ...c, isDeleted: true } : c));
      const updatedAllCertifications = { ...(onboarding.allCertifications || {}), items: updatedItems };

      dispatch(
        setOnboarding({
          allCertifications: updatedAllCertifications,
          uploadingDetails: {
            ...onboarding.uploadingDetails,
            isOffline: true,
            pendingUploads: { ...onboarding.uploadingDetails?.pendingUploads, certifications: true },
          },
        }),
      );
      onDeleteSuccess();
      onClose();
      return;
    }
    dispatch(deleteCertification({ workerId, certificateId: certificate.id, languageCode }))
      .then(res => {
        if (res.type.includes('fulfilled')) {
          onDeleteSuccess();
          onClose();
        } else {
          enqueueSnackbar(t('certifications.deleteCertificateModal.errors.failedToDelete'), { variant: 'error' });
        }
      })
      .catch(() => {
        enqueueSnackbar(t('certifications.deleteCertificateModal.errors.failedToDelete'), { variant: 'error' });
      });
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Modal
      isVisible={isVisible}
      modalStyleClass={classes.modal}
      modalTitleStyleClass={classes.modalTitle}
      modalContentStyleClass={classes.modalContent}
      modalActionsStyleClass={classes.modalActions}
      Content={
        <>
          <div className={classes.imageParent}>
            <ExclamationRound />
          </div>
          <p className={classes.title}>{t('certifications.deleteCertificateModal.title')}</p>
          <p className={classes.warningText}>
            {t('certifications.deleteCertificateModal.aboutToDelete')}
            <span className={classes.certificationName}> "{certificate.certification.name}" </span>
            {t('certifications.deleteCertificateModal.certification')}{' '}
            {t('certifications.deleteCertificateModal.mayAffectBadge')}
          </p>
        </>
      }
      actionButtons={[
        {
          label: t('common.cancel'),
          variant: 'outlined',
          styleClass: classes.button,
          onClick: onClose,
          testId: 'delete-cert-cancel-button',
        },
        {
          label: t('common.delete'),
          styleClass: classes.button,
          isLoading: isDeleteCertificationLoading,
          onClick: onDeleteCertification,
          testId: 'delete-cert-confirm-button',
        },
      ]}
      onClose={onClose}
    />
  );
};

export default React.memo(DeleteCertificationModal);
